import { FileWarning, X } from "lucide-react";
import { useState } from "react";
import { LoadingPanel } from "@/components/ui/loading-state";
import { cn } from "@/lib/utils";
import type { ClaudiaComposerAttachment } from "../hooks/use-claudia-attachments";
import { AttachmentDocumentChip } from "./attachment-document-chip";
import {
  AttachmentPreviewDialog,
  type AttachmentPreviewDialogItem,
} from "./attachment-preview-dialog";

/**
 * The composer attachment tray (design "Message Composer — Attachments", node `tqrJG`). Renders a
 * single non-wrapping, horizontally scrollable row of attachment items above the input. Images are
 * 64×64 thumbnails; documents are chips with a type tile, name and size. Everything is
 * square-cornered and neutral — accent blue is reserved for upload progress and the send action.
 * The remove (✕) is hidden until the item is hovered or focused.
 */
export function AssistantComposerAttachmentsStrip({
  attachments,
  onRemove,
}: {
  attachments: ClaudiaComposerAttachment[];
  onRemove: (localId: string) => void;
}) {
  const [preview, setPreview] = useState<AttachmentPreviewDialogItem | null>(null);

  if (attachments.length === 0) {
    return null;
  }

  return (
    <>
      <div className="flex gap-2 overflow-x-auto">
        {attachments.map((attachment) => {
          const isImage = attachment.kind === "image";
          const isUploading = attachment.status === "uploading";
          const isFailed = attachment.status === "failed";
          const removeButton = (
            <button
              type="button"
              aria-label={`Remove ${attachment.fileName}`}
              onClick={() => onRemove(attachment.localId)}
              className={cn(
                "flex items-center justify-center opacity-0 transition-opacity",
                "group-hover:opacity-100 focus-visible:opacity-100 focus-visible:outline-none",
                isImage
                  ? "absolute right-1 top-1 size-4 bg-black/70 text-white"
                  : "size-5 shrink-0 text-muted-foreground hover:text-foreground"
              )}
            >
              <X className={isImage ? "size-2.5" : "size-3"} />
            </button>
          );

          if (isImage) {
            const canPreview = attachment.status === "ready";
            return (
              <div
                key={attachment.localId}
                className={cn(
                  "group relative size-16 shrink-0 overflow-hidden border bg-muted",
                  isFailed && "border-destructive"
                )}
              >
                {/* The thumbnail itself opens the lightbox; the ✕ is a sibling (not nested) to keep
                  the markup valid and the two hit targets independent. */}
                <button
                  type="button"
                  onClick={() =>
                    setPreview({ fileName: attachment.fileName, url: attachment.previewUrl })
                  }
                  disabled={!canPreview}
                  aria-label={`Preview ${attachment.fileName}`}
                  className="block size-full cursor-zoom-in disabled:cursor-default"
                >
                  <img
                    src={attachment.previewUrl}
                    alt={attachment.fileName}
                    className="size-full object-contain"
                    draggable={false}
                  />
                </button>
                {isUploading ? (
                  <LoadingPanel className="pointer-events-none absolute inset-0 bg-background/75" />
                ) : null}
                {isFailed ? (
                  <div className="pointer-events-none absolute inset-0 flex items-center justify-center bg-destructive/80 text-destructive-foreground">
                    <FileWarning className="size-4" />
                  </div>
                ) : null}
                {removeButton}
              </div>
            );
          }

          return (
            <AttachmentDocumentChip
              key={attachment.localId}
              fileName={attachment.fileName}
              mimeType={attachment.mimeType}
              fileSizeBytes={attachment.fileSizeBytes}
              status={attachment.status === "ready" ? "attached" : attachment.status}
              progress={attachment.uploadProgress}
              error={attachment.error}
              onRemove={() => onRemove(attachment.localId)}
            />
          );
        })}
      </div>
      <AttachmentPreviewDialog
        attachment={preview}
        onOpenChange={(open) => !open && setPreview(null)}
      />
    </>
  );
}
